// ReceiptInvoiceModal.jsx
import React, { useRef } from 'react';
import Modal from '../components/ui/Modal';
import Button from '../components/ui/Button';
import { Printer } from 'lucide-react'; // optional icon for thermal button

const ReceiptInvoiceModal = ({ isOpen, onClose, receiptData }) => {
    const receiptRef = useRef();

    if (!isOpen || !receiptData) {
        return null;
    }

    const printReceipt = () => {
        const printWindow = window.open('', '_blank');

        if (!printWindow) {
            console.error("Could not open print window. Pop-ups might be blocked.");
            return;
        }

        const receiptContentHtml = receiptRef.current ? receiptRef.current.innerHTML : '';

        const fullHtml = `
      <html>
      <head>
        <title>Receipt</title>
        <script src="https://cdn.tailwindcss.com"></script>
        <style>
          @page { size: auto; margin: 0; } 
          body {
            width: 100%;
            min-height: 100vh;
            margin: 0;
            padding: 5mm;
            font-family: 'monospace', 'Courier New', Courier, monospace;
            font-size: 10px;
            color: #000;
            background-color: #fff;
            line-height: 1.2;
          }
          .receipt-container {
            width: 100%;
            box-sizing: border-box;
          }
          .text-center { text-align: center; }
          .text-left { text-align: left; }
          .text-right { text-align: right; }
          .font-bold { font-weight: bold; }
          .text-xs { font-size: 0.8em; } 
          .text-sm { font-size: 1em; }
          .text-base { font-size: 1.2em; }
          .my-1 { margin-top: 4px; margin-bottom: 4px; }
          .py-1 { padding-top: 4px; padding-bottom: 4px; }
          .border-t-dashed { border-top: 1px dashed #000; }
          .border-b-dashed { border-bottom: 1px dashed #000; }
          .pb-1 { padding-bottom: 4px; }
          .pt-1 { padding-top: 4px; }
          .flex { display: flex; }
          .justify-between { justify-content: space-between; }
          .w-full { width: 100%; }
          .w-1\\/2 { width: 50%; }
          .w-1\\/6 { width: 16.666667%; }
          .uppercase { text-transform: uppercase; }
          .break-words { word-break: break-all; }
          @media print {
            .no-print { display: none !important; }
          }
        </style>
      </head>
      <body>
        <div class="receipt-container">
          ${receiptContentHtml}
        </div>
      </body>
      </html>
    `;

        printWindow.document.write(fullHtml);
        printWindow.document.close();

        printWindow.onload = function () {
            setTimeout(() => {
                printWindow.focus();
                printWindow.print();
                printWindow.close();
            }, 200);
        };
    };

    // ──────────────────────────────────────────────────────────────
    // THERMAL PRINTING – PLAIN TEXT VERSION (no Base64, no gibberish)
    // ──────────────────────────────────────────────────────────────
    const handleThermalPrint = () => {
        if (!receiptData) {
            alert("No receipt data available");
            return;
        }

        // Build plain-text receipt that matches your original layout as closely as possible
        const receiptText = `
${receiptData.shopName || 'MY SHOP'.padEnd(30, ' ').slice(0, 30)}
Cashier: ${receiptData.cashierName || 'N/A'}
Customer: ${receiptData.customerInfo.name || 'N/A'}
Date: ${receiptData.date}
Order ID: ${receiptData.orderId}
----------------------------------------
${receiptData.orderItems
                .map(item => {
                    const name = (item.name || 'Item').slice(0, 14).padEnd(14);
                    const qtyPrice = `${item.quantity}x${Number(item.pricePerUnit || 0).toFixed(0)}`;
                    const symbol = item.priceSymbol || '₦';
                    const total = `${symbol}${Number(item.totalPrice || (item.quantity * item.pricePerUnit) || 0).toFixed(0)}`;
                    const details = qtyPrice.padEnd(12) + total;
                    return `${name} ${details}`;
                })
                .join('\n')}
----------------------------------------
Subtotal:          ₦${Number(receiptData.subtotal || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
Tax:               ₦${Number(receiptData.taxAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
Discount:         -₦${Number(receiptData.totalDiscount || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
TOTAL:             ₦${Number(receiptData.finalAmountDue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
PAID:              ₦${Number(receiptData.totalPaid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
CHANGE:            ₦${Number(receiptData.changeDue || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
----------------------------------------
Payment: ${receiptData.paymentStatus || 'Cash'}
${receiptData.paymentStatus === 'credit' ? `Due Date: ${receiptData.paymentDueDate || 'N/A'}` : ''}
Thank you for your business!
Please come again.
All sales final.
        `.trim();

        // Encode for URL
        const encodedText = encodeURIComponent(receiptText);

        // Plain text RawBT deep link
        const rawbtUrl = `rawbt://print?text=${encodedText}`;

        // Debug log
        console.log("Sending plain text to RawBT:", rawbtUrl);

        // Trigger RawBT
        window.location.href = rawbtUrl;

        // Feedback to user
        setTimeout(() => {
            alert(
                "Receipt sent to RawBT!\n\n" +
                "Expected result:\n" +
                "• RawBT should open\n" +
                "• Printer should print clean text (no random letters)\n\n" +
                "If still random letters:\n" +
                "1. Open RawBT app manually\n" +
                "2. Go to Settings → check printer connection\n" +
                "3. Try test print from inside RawBT\n" +
                "4. Confirm printer supports plain text (most do)"
            );
        }, 1000);
    };

    // Helper to format currency (used in browser view)
    const formatCurrency = (amountInNaira) => {
        return `₦${(parseFloat(amountInNaira)).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="Receipt/Invoice"
            overlayClassName="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
            contentClassName="bg-white rounded-lg shadow-xl p-6 max-w-sm w-full mx-auto flex flex-col"
        >
            {/* Receipt content (for browser print and modal display) */}
            <div ref={receiptRef} className="receipt-content font-mono text-xs text-gray-900 leading-tight">
                {/* Receipt Header */}
                <div className="text-center mb-4">
                    <h3 className="font-bold text-base uppercase">{receiptData.shopName}</h3>
                    <p className="text-xs">{receiptData.shopAddress}</p>
                    <p className="text-xs my-1">Date: {receiptData.date}</p>
                    <p className="text-xs my-1">Order ID: {receiptData.orderId}</p>
                    {receiptData.cashierName && (
                        <p className="text-xs my-1">Cashier: {receiptData.cashierName}</p>
                    )}
                    {receiptData.transactionReference && (
                        <p className="text-xs my-1 break-words">Ref: {receiptData.transactionReference}</p>
                    )}
                    <div className="border-b-dashed border-gray-400 my-2"></div>
                </div>

                {/* Customer Info */}
                {receiptData.customerInfo && (
                    <div className="mb-4">
                        <p className="font-bold text-sm">Customer:</p>
                        <p className="text-xs">{receiptData.customerInfo.name}</p>
                        {receiptData.customerInfo.phone && <p className="text-xs">Phone: {receiptData.customerInfo.phone}</p>}
                        {receiptData.customerInfo.email && <p className="text-xs">Email: {receiptData.customerInfo.email}</p>}
                        <div className="border-b-dashed border-gray-400 my-2"></div>
                    </div>
                )}

                {/* Order Items */}
                <div className="mb-4">
                    <div className="flex justify-between font-bold text-sm pb-1 border-b-dashed border-gray-400">
                        <span className="w-1/2 text-left">Item</span>
                        <span className="w-1/6 text-center">Qty</span>
                        <span className="w-1/6 text-right">Price</span>
                        <span className="w-1/6 text-right">Total</span>
                    </div>
                    {receiptData.orderItems.map((item, index) => (
                        <div key={index} className="flex justify-between py-1 border-b-dashed border-gray-200 last:border-b-0">
                            <span className="w-1/2 text-left">{item.name}</span>
                            <span className="w-1/6 text-center">{item.quantity}</span>
                            <span className="w-1/6 text-right">{item.priceSymbol}{item.pricePerUnit.toFixed(2)}</span>
                            <span className="w-1/6 text-right">{item.priceSymbol}{item.totalPrice.toFixed(2)}</span>
                        </div>
                    ))}
                    <div className="border-b-dashed border-gray-400 mt-2"></div>
                </div>

                {/* Summary */}
                <div className="mb-4">
                    <div className="flex justify-between my-1">
                        <span className="font-bold">Subtotal:</span>
                        <span>{formatCurrency(receiptData.subtotal)}</span>
                    </div>
                    <div className="flex justify-between my-1 font-bold text-base">
                        <span>TOTAL:</span>
                        <span>{formatCurrency(receiptData.subtotal)}</span>
                    </div>
                    <div className="flex justify-between my-1">
                        <span className="font-bold">Amount Paid:</span>
                        <span>{formatCurrency(receiptData.totalPaid)}</span>
                    </div>
                    {receiptData.changeDue > 0 && (
                        <div className="flex justify-between my-1 font-bold text-green-700">
                            <span>CHANGE DUE:</span>
                            <span>{formatCurrency(receiptData.changeDue)}</span>
                        </div>
                    )}
                    <div className="flex justify-between my-1">
                        <span className="font-bold">Payment Status:</span>
                        <span>{receiptData.paymentStatus}</span>
                    </div>
                    <div className="border-b-dashed border-gray-400 my-2"></div>
                </div>

                {/* Receipt Footer */}
                <div className="text-center text-xs">
                    <p className="font-bold mb-1">THANK YOU FOR YOUR BUSINESS!</p>
                    <p>Please come again.</p>
                    <p className="mt-2">All sales final.</p>
                </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-center gap-4 mt-6 no-print flex-wrap">
                <Button
                    onClick={printReceipt}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md shadow"
                >
                    Print (A4/Paper)
                </Button>

                <Button
                    onClick={handleThermalPrint}
                    className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md shadow flex items-center gap-2"
                >
                    <Printer size={18} /> Print Thermal
                </Button>

                <Button
                    onClick={onClose}
                    className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-md shadow"
                >
                    Close
                </Button>
            </div>
        </Modal>
    );
};

export default ReceiptInvoiceModal;